import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, X, Loader2, ArrowRight, Smartphone, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVERFIT_INVITE_LINK = "https://invite.everfit.io/7DI0G65GXZ";

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [showEverfitPopup, setShowEverfitPopup] = useState(false);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;
      setPaymentData(data);
      
      if (data.payment_status === "paid") {
        setStatus("success");
        // Show Everfit popup after successful payment
        setTimeout(() => setShowEverfitPopup(true), 1500);
      } else if (data.status === "expired") {
        setStatus("expired");
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setStatus("error");
    }
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-8 border-2 border-neutral-700 flex items-center justify-center animate-pulse">
              <Loader2 size={40} className="animate-spin text-neutral-500" />
            </div>
            <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              PROCESSING PAYMENT
            </h1>
            <p className="font-manrope text-neutral-400 text-lg">
              Please wait while we confirm your payment...
            </p>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-8 bg-mvision-gold text-black flex items-center justify-center">
              <Check size={40} />
            </div>
            <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4" data-testid="payment-success-title">
              PAYMENT SUCCESSFUL
            </h1>
            <p className="font-manrope text-neutral-400 text-lg mb-8">
              Thank you for your purchase! Your subscription is now active.
            </p>
            
            {paymentData && (
              <div className="bg-neutral-950 border border-mvision-gold/30 p-6 mb-8 text-left max-w-md mx-auto">
                <h3 className="font-oswald uppercase tracking-wider text-sm text-mvision-gold mb-4">ORDER DETAILS</h3>
                <div className="space-y-3">
                  <p className="font-manrope">
                    <span className="text-neutral-500">Package:</span> {paymentData.metadata?.package_name}
                  </p>
                  <p className="font-manrope">
                    <span className="text-neutral-500">Amount:</span> ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </p>
                  <p className="font-manrope">
                    <span className="text-neutral-500">Status:</span> <span className="text-mvision-gold">Paid</span>
                  </p>
                </div>
              </div>
            )}

            {/* Everfit CTA */}
            <div className="bg-gradient-to-r from-mvision-gold/20 to-mvision-gold/10 border border-mvision-gold/30 p-6 mb-8 max-w-md mx-auto">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Smartphone size={20} className="text-mvision-gold" />
                <h3 className="font-oswald uppercase tracking-wider text-sm">NEXT STEP: GET THE APP</h3>
              </div>
              <p className="font-manrope text-neutral-400 text-sm mb-4">
                Download <span className="text-mvision-gold font-semibold">Everfit</span> to access your workouts, nutrition plans, and chat with your coach!
              </p>
              <a 
                href={EVERFIT_INVITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-mvision-gold text-black font-oswald uppercase tracking-wider text-sm px-6 py-3 hover:bg-mvision-goldLight transition-colors"
                data-testid="everfit-invite-btn"
              >
                JOIN EVERFIT
                <ExternalLink size={16} />
              </a>
            </div>

            <p className="font-manrope text-neutral-500 text-sm mb-8">
              A confirmation email will be sent to you shortly. We'll be in touch within 24 hours to get you started.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" data-testid="back-home-btn">
                <button className="btn-secondary">BACK TO HOME</button>
              </Link>
              <Link to="/booking" data-testid="book-session-btn">
                <button className="btn-primary flex items-center gap-2">
                  BOOK YOUR FIRST SESSION
                  <ArrowRight size={20} />
                </button>
              </Link>
            </div>
          </div>
        );

      case "expired":
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-8 border-2 border-neutral-700 flex items-center justify-center">
              <X size={40} className="text-neutral-500" />
            </div>
            <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              SESSION EXPIRED
            </h1>
            <p className="font-manrope text-neutral-400 text-lg mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/#pricing" data-testid="try-again-btn">
              <button className="btn-primary">TRY AGAIN</button>
            </Link>
          </div>
        );

      case "timeout":
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-8 border-2 border-neutral-700 flex items-center justify-center">
              <Loader2 size={40} className="text-neutral-500" />
            </div>
            <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              VERIFICATION PENDING
            </h1>
            <p className="font-manrope text-neutral-400 text-lg mb-8">
              We're still verifying your payment. Please check your email for confirmation.
            </p>
            <Link to="/" data-testid="back-home-btn">
              <button className="btn-secondary">BACK TO HOME</button>
            </Link>
          </div>
        );

      case "error":
      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-8 border-2 border-red-500 flex items-center justify-center">
              <X size={40} className="text-red-500" />
            </div>
            <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase tracking-tighter mb-4">
              SOMETHING WENT WRONG
            </h1>
            <p className="font-manrope text-neutral-400 text-lg mb-8">
              We couldn't verify your payment. Please contact support if you believe this is an error.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/#pricing" data-testid="try-again-btn">
                <button className="btn-secondary">TRY AGAIN</button>
              </Link>
              <Link to="/#contact" data-testid="contact-support-btn">
                <button className="btn-primary">CONTACT SUPPORT</button>
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main className="pt-32 pb-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {renderContent()}
        </div>
      </main>
      
      {/* Everfit Popup */}
      {showEverfitPopup && (
        <EverfitPopup onClose={() => setShowEverfitPopup(false)} />
      )}
      
      <Footer />
    </div>
  );
};

const EverfitPopup = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-black border border-mvision-gold max-w-lg w-full animate-fade-in" data-testid="everfit-popup">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        <div className="p-8 text-center">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-6 bg-mvision-gold text-black rounded-full flex items-center justify-center">
            <Smartphone size={32} />
          </div>
          
          <h2 className="font-oswald text-2xl md:text-3xl font-bold uppercase tracking-tighter mb-4">
            ONE MORE STEP!
          </h2>
          
          <p className="font-manrope text-neutral-400 mb-6">
            To access your personalized workouts, nutrition plans, and direct messaging with your coach, 
            join me on <span className="text-mvision-gold font-semibold">Everfit</span> - our official training app.
          </p>

          <div className="bg-neutral-950 border border-neutral-800 p-4 mb-6">
            <p className="font-manrope text-sm text-neutral-500 mb-2">With Everfit you get:</p>
            <ul className="text-left space-y-2">
              <li className="flex items-center gap-2 text-sm font-manrope text-neutral-300">
                <Check size={16} className="text-mvision-gold flex-shrink-0" />
                Custom workout programs
              </li>
              <li className="flex items-center gap-2 text-sm font-manrope text-neutral-300">
                <Check size={16} className="text-mvision-gold flex-shrink-0" />
                Progress & habit tracking
              </li>
              <li className="flex items-center gap-2 text-sm font-manrope text-neutral-300">
                <Check size={16} className="text-mvision-gold flex-shrink-0" />
                Nutrition & meal plans
              </li>
              <li className="flex items-center gap-2 text-sm font-manrope text-neutral-300">
                <Check size={16} className="text-mvision-gold flex-shrink-0" />
                Direct messaging with your coach
              </li>
            </ul>
          </div>
          
          <a 
            href={EVERFIT_INVITE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full bg-mvision-gold text-black font-oswald uppercase tracking-wider py-4 hover:bg-mvision-goldLight transition-colors"
            data-testid="everfit-popup-btn"
          >
            <Smartphone size={20} />
            JOIN EVERFIT NOW
            <ExternalLink size={16} />
          </a>
          
          <button 
            onClick={onClose}
            className="mt-4 font-manrope text-sm text-neutral-500 hover:text-white transition-colors"
          >
            I'll do this later
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
